'use client';

import { useState } from 'react';
import {
  trackBudgetCalculatorCalculate,
  trackBudgetCalculatorEditAmounts,
  trackBudgetCalculatorReset,
} from 'components/analytics/eventHandlerTracking';
import { CalculatorShell } from 'components/Widget/shared/CalculatorShell';
import { parseCurrency, toCents } from 'components/Widget/shared/currency';
import { deferTracking } from 'components/Widget/shared/deferTracking';
import { useCurrencyFields } from 'components/Widget/shared/useCurrencyFields';
import { usePanelSwapFocus } from 'components/Widget/shared/usePanelSwapFocus';
import type { CalculatorFieldErrors, CalculatorFieldValues } from 'components/Widget/shared/types';
import { calculateBudget } from './budget';
import { BudgetCalculatorForm } from './BudgetCalculatorForm';
import { BudgetCalculatorResult } from './BudgetCalculatorResult';
import { BudgetCalculatorSummary } from './BudgetCalculatorSummary';
import {
  BUDGET_FIELDS,
  DISCLAIMER,
  INCOME_ERROR,
  INCOME_FIELD,
  INITIAL_VALUES,
  MAX_FIELD_VALUE,
  RANGE_ERROR,
} from './constants';
import type { BudgetState } from './types';

const INITIAL_STATE: BudgetState = { errors: {}, result: null };

const validateValues = (values: CalculatorFieldValues): CalculatorFieldErrors => {
  const errors: CalculatorFieldErrors = {};

  // Sanitization strips negative signs, so only the ceiling needs checking.
  BUDGET_FIELDS.forEach(({ label }) => {
    if (parseCurrency(values[label]) > MAX_FIELD_VALUE) errors[label] = RANGE_ERROR;
  });

  const incomeInCents = toCents(parseCurrency(values[INCOME_FIELD.label]));

  if (incomeInCents <= 0) errors[INCOME_FIELD.label] = INCOME_ERROR;

  return errors;
};

export const BudgetCalculator = () => {
  const [state, setState] = useState<BudgetState>(INITIAL_STATE);
  const { values, normalize, commit, reset, fieldProps } = useCurrencyFields(INITIAL_VALUES);
  const { formRef, summaryRef, markPanelSwap } = usePanelSwapFocus(state.result !== null);

  const handleCalculate = () => {
    const normalizedValues = normalize();
    const errors = validateValues(normalizedValues);
    const hasErrors = Object.keys(errors).length > 0;
    const result = hasErrors
      ? null
      : calculateBudget(normalizedValues, parseCurrency(normalizedValues[INCOME_FIELD.label]));

    // A passing Calculate swaps the form out, so the field Enter was pressed in has to be settled for display.
    commit({ formatFocusedField: !hasErrors });
    setState({ errors, result });

    deferTracking(() => {
      trackBudgetCalculatorCalculate();
    });

    if (result === null) return;

    markPanelSwap();
  };

  /** Report which clear action ran before both paths share initial state. */
  const handleClear = (label: 'Reset' | 'Start Over') => {
    // Only Start over replaces a panel.
    markPanelSwap(state.result !== null);

    setState(INITIAL_STATE);
    reset();

    deferTracking(() => {
      trackBudgetCalculatorReset({ label });
    });
  };

  const handleEdit = () => {
    markPanelSwap();

    setState(previous => ({ ...previous, result: null }));

    deferTracking(() => {
      trackBudgetCalculatorEditAmounts();
    });
  };

  return (
    <CalculatorShell
      title="Budget Calculator"
      disclaimer={DISCLAIMER}
      summaryRef={summaryRef}
      summary={state.result !== null && <BudgetCalculatorSummary result={state.result} />}>
      {state.result === null ? (
        <BudgetCalculatorForm
          ref={formRef}
          errors={state.errors}
          fieldProps={fieldProps}
          onCalculate={handleCalculate}
          onReset={() => handleClear('Reset')}
        />
      ) : (
        <BudgetCalculatorResult
          result={state.result}
          onEdit={handleEdit}
          onStartOver={() => handleClear('Start Over')}
        />
      )}
    </CalculatorShell>
  );
};
